import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Badge } from '@core-ui/Components/ui/badge';
import { cn } from '@core-ui-lib/utils';

import { Loader2, CirclePlus } from 'lucide-react';

import { SearchInput } from '@ui/shared/searchInput';
import { usePaginationData } from '@hooks/usePaginationData';
import { PaginationControls } from '@ui/shared/paginationControls';
import useIntentApi from '@features/intent/hooks/useIntentApi';
import { useSessionStore } from '@features/auth';
import { useFeatureFlagsStore } from '@store/featureFlags';
import { IntentSources } from '@types';
import { logAndToast } from '@lib/logger/logAndToast';
import { logSuccess } from '@lib/logger/logSuccess';

import type { ScriptType } from '../../types/wizard.types';
import useWizardStore from '../../store/useWizardStore';
import IntentPreviewModal from '../IntentPreviewModal';
import { DiscardIntentDialog } from '@features/intent/components/DiscardIntentDialog.tsx';

interface SelectIntentStepProps {
  onCreateHandlerChange?: (handler: (() => void) | null) => void;
  onCanCreateChange?: (canCreate: boolean) => void;
}

export default function SelectIntentStep ({
  onCreateHandlerChange,
  onCanCreateChange,
}: SelectIntentStepProps): React.JSX.Element {
  const { t } = useTranslation();
  const { currentUser } = useSessionStore();
  const { getScriptsByOrgId, createScript, sketchWithAI } = useIntentApi();
  const featureFlags = useFeatureFlagsStore((s) => s.flags);

  const { selectedIntent, setIntent, stepMode, setStepMode, nextStep } = useWizardStore();

  const [intents, setIntents] = useState<ScriptType[]>([]);
  const [filteredIntents, setFilteredIntents] = useState<ScriptType[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [previewIntent, setPreviewIntent] = useState<ScriptType | null>(null);

  // Create mode state
  const [createTitle, setCreateTitle] = useState('');
  const [createBody, setCreateBody] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isGeneratingAI, setIsGeneratingAI] = useState(false);
  const [createErrors, setCreateErrors] = useState<{ title?: string; body?: string }>({});
  const capturePrompt = useRef('');

  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [pendingMode, setPendingMode] = useState<'select' | 'create' | null>(null);
  const activeMode = stepMode[1] ?? 'select';

  const intentSchema = z.object({
    title: z.string().min(1, t('intentForm.Title is required')),
    body: z.string().min(1, t('intentForm.Body is required')),
  });

  const isFormValid = createTitle.trim() !== '' && createBody.trim() !== '' &&
    createErrors.title == null && createErrors.body == null;

  // Report canCreate to parent — use ref to avoid re-render loops
  const onCanCreateChangeRef = useRef(onCanCreateChange);
  onCanCreateChangeRef.current = onCanCreateChange;

  useEffect(() => {
    if (activeMode === 'create') {
      onCanCreateChangeRef.current?.(isFormValid && !isSaving);
    } else {
      onCanCreateChangeRef.current?.(false);
    }
  }, [activeMode, isFormValid, isSaving]);

  const fetchIntents = useCallback(async (): Promise<void> => {
    if (currentUser?.organizationId === undefined) {
      return;
    }

    setIsLoading(true);
    const result = await getScriptsByOrgId(currentUser.organizationId);
    if (result.success && result.data !== undefined) {
      setIntents(result.data);
      setFilteredIntents(result.data);
    }
    setIsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.organizationId]);

  useEffect(() => {
    void fetchIntents();
  }, [fetchIntents]);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredIntents(intents);
    } else {
      const query = searchQuery.toLowerCase();
      setFilteredIntents(
        intents.filter(
          (intent) =>
            intent.title.toLowerCase().includes(query) ||
            intent.body.toLowerCase().includes(query),
        ),
      );
    }
  }, [searchQuery, intents]);

  const {
    currentPage,
    setCurrentPage,
    rowsPerPage,
    setRowsPerPage,
    currentData: paginatedIntents,
    totalItems,
  } = usePaginationData<ScriptType>({
    mode: 'client',
    data: filteredIntents,
    initialRowsPerPage: 5,
  });
  const prevModeRef = useRef(activeMode);

  useEffect(() => {
    if (prevModeRef.current !== activeMode) {
      setShowDiscardModal(true);
      prevModeRef.current = activeMode;
    }
  }, []);

  const handleSelectIntent = (intent: ScriptType): void => {
    if (selectedIntent?.id === intent.id) {
      setIntent(null);
    } else {
      setIntent(intent);
    }
  };

  const getLanguageBadge = (body: string): string => {
    const spanishPattern = /hola|buenos|gracias|llamando|nombre/i;
    return spanishPattern.test(body) ? 'ES' : 'EN';
  };

  const truncateText = (text: string, maxLength: number): string => {
    if (text.length <= maxLength) {
      return text;
    }
    return `${text.substring(0, maxLength)}...`;
  };

  const validateCreateField = useCallback(
    (field: 'title' | 'body', value: string) => {
      const partial = intentSchema.pick({ [field]: true });
      const result = partial.safeParse({ [field]: value });
      setCreateErrors((prev) => ({
        ...prev,
        [field]: result.success ? undefined : result.error.errors[0]?.message,
      }));
    },
    [t],
  );

  // Keep refs to form values so save always reads latest state
  const createTitleRef = useRef(createTitle);
  createTitleRef.current = createTitle;
  const createBodyRef = useRef(createBody);
  createBodyRef.current = createBody;

  const handleCreateSave = useCallback(async (): Promise<void> => {
    const orgId = currentUser?.organizationId;
    if (orgId == null) {
      return;
    }

    const titleVal = createTitleRef.current;
    const bodyVal = createBodyRef.current;

    const result = intentSchema.safeParse({ title: titleVal, body: bodyVal });
    if (!result.success) {
      const fieldErrors: { title?: string; body?: string } = {};
      result.error.errors.forEach((e) => {
        if (e.path[0] === 'title') {
          fieldErrors.title = e.message;
        }
        if (e.path[0] === 'body') {
          fieldErrors.body = e.message;
        }
      });
      setCreateErrors(fieldErrors);
      return;
    }

    setIsSaving(true);
    const apiResult = await createScript({
      title: titleVal,
      body: bodyVal,
      organizationId: orgId,
      source: IntentSources.HUMAN,
      updatedAt: new Date().toISOString(),
    });

    if (apiResult.success && apiResult.data !== undefined) {
      await logSuccess(
        t('intentForm.Intent created successfully'),
        'SelectIntentStep.createIntent',
        { intentId: apiResult.data.id },
      );
      void fetchIntents();
      setIntent(apiResult.data);
      setStepMode(1, 'select');
      setCreateTitle('');
      setCreateBody('');
      setCreateErrors({});
      nextStep();
    } else {
      await logAndToast({
        message: apiResult.error?.message ?? t('intentForm.Failed to save intent'),
        source: 'SelectIntentStep.createIntent',
        toast: true,
        level: 'error',
      });
    }
    setIsSaving(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.organizationId]);

  // Register create handler for the WizardNavigation button
  const onCreateHandlerChangeRef = useRef(onCreateHandlerChange);
  onCreateHandlerChangeRef.current = onCreateHandlerChange;

  useEffect(() => {
    if (activeMode === 'create') {
      onCreateHandlerChangeRef.current?.(() => {
        void handleCreateSave();
      });
    } else {
      onCreateHandlerChangeRef.current?.(null);
    }
  }, [activeMode]);

  const handleGenerateAI = useCallback(async (): Promise<void> => {
    const userId = currentUser?.userId;
    if (userId == null) {
      return;
    }

    setIsGeneratingAI(true);
    const prompt = capturePrompt.current !== '' ? capturePrompt.current : createTitle;
    const result = await sketchWithAI(String(userId), prompt);
    if (result.data != null) {
      setCreateTitle(result.data.title);
      setCreateBody(result.data.body);
    }
    setIsGeneratingAI(false);
  }, [currentUser?.userId, sketchWithAI, createTitle]);

  const handleTabChange = (mode: string): void => {
    const hasContent = createTitle.trim() !== '' || createBody.trim() !== '';
    if (activeMode === 'create' && mode === 'select' && hasContent) {
      setPendingMode(mode as 'select' | 'create');
      setShowDiscardModal(true);
    } else {
      setStepMode(1, mode as 'select' | 'create');
    }
  };

  const confirmDiscard = (): void => {
    if (pendingMode !== null) {
      setStepMode(1, pendingMode);
      setCreateTitle('');
      setCreateBody('');
      setCreateErrors({});
      setPendingMode(null);
    }
  };

  return (
    <div>
      <div className="mb-4">
        <h3 className="text-lg font-semibold">{t('wizard.selectIntent.title')}</h3>
        <p className="text-sm text-muted-foreground">
          {activeMode === 'create'
            ? t('wizard.inlineCreate.selectOrCreate', 'Select an existing script or create a new one')
            : t('wizard.selectIntent.description')}
        </p>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-4">
        <button
          type="button"
          onClick={() => {
            handleTabChange('select'); 
          }}
          className={cn(
            'px-4 py-2 rounded-md text-sm font-medium transition-colors',
            activeMode === 'select'
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted/60 text-muted-foreground hover:bg-muted',
          )}
        >
          {t('wizard.inlineCreate.selectExisting', 'Select Existing')}
        </button>
        <button
          type="button"
          onClick={() => {
            handleTabChange('create'); 
          }}
          className={cn(
            'flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors',
            activeMode === 'create'
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted/60 text-muted-foreground hover:bg-muted',
          )}
        >
          <CirclePlus className="mr-2 h-4 w-4" />
          {t('wizard.inlineCreate.createNew', 'Create New')}
        </button>
      </div>

      {activeMode === 'select' ? (
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <SearchInput
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                }}
                onClear={() => {
                  setSearchQuery('');
                }}
                placeholder={t('wizard.selectIntent.searchPh')}
              />
            </div>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : filteredIntents.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">
              {t('wizard.selectIntent.empty')}
            </div>
          ) : (
            <>
              <div className="rounded-lg border border-border">
                {paginatedIntents.map((intent, index) => (
                  <button
                    data-qa-id={`intent-${index}`}
                    key={intent.id}
                    type="button"
                    onClick={() => {
                      handleSelectIntent(intent);
                    }}
                    className={cn(
                      'w-full flex items-start gap-4 p-4 text-left transition-colors hover:bg-muted/40 border-b border-border/60 last:border-b-0',
                      selectedIntent?.id === intent.id && 'bg-muted/60',
                    )}
                  >
                    <div
                      className={cn(
                        'mt-1 h-5 w-5 rounded-full border-2 flex-shrink-0 transition-colors',
                        selectedIntent?.id === intent.id
                          ? 'border-primary bg-primary'
                          : 'border-muted-foreground',
                      )}
                    >
                      {selectedIntent?.id === intent.id && (
                        <div className="h-full w-full flex items-center justify-center">
                          <div className="h-2 w-2 rounded-full bg-white" />
                        </div>
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{intent.title}</span>
                        <Badge variant="secondary" className="rounded-full text-xs">
                          {getLanguageBadge(intent.body)}
                        </Badge>
                      </div>
                      <p className="mt-1 text-sm text-muted-foreground break-all">
                        {truncateText(intent.body, 80)}
                      </p>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setPreviewIntent(intent);
                        }}
                        className="mt-1 text-sm text-primary hover:underline"
                      >
                        {t('wizard.selectIntent.viewMore')}
                      </button>
                      <div className="mt-1 text-xs text-muted-foreground">
                        {t('wizard.selectIntent.updated')} {new Date(intent.updatedAt).toLocaleDateString()}
                      </div>
                    </div>

                    {selectedIntent?.id === intent.id && (
                      <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                        {t('wizard.selected')}
                      </Badge>
                    )}
                  </button>
                ))}
              </div>

              {totalItems > 0 && (
                <PaginationControls
                  totalItems={totalItems}
                  currentPage={currentPage}
                  onPageChange={(p) => {
                    setCurrentPage(Math.max(1, Math.min(p, Math.ceil(totalItems / rowsPerPage))));
                  }}
                  rowsPerPage={rowsPerPage}
                  setRowsPerPage={(n) => {
                    setRowsPerPage(n);
                    setCurrentPage(1);
                  }}
                  rowsPerPageOptions={[5, 10, 15]}
                />
              )}
            </>
          )}
        </div>
      ) : (
        /* Create New tab */
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-muted-foreground">
              {t('wizard.inlineCreate.intent.titleLabel', 'Title')} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={createTitle}
              onChange={(e) => {
                setCreateTitle(e.target.value);
                capturePrompt.current = e.target.value;
                validateCreateField('title', e.target.value);
              }}
              placeholder={t('intentForm.Intent name', 'Intent name')}
              disabled={isSaving || isGeneratingAI}
              className={cn(
                'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50',
                !(createErrors.title == null) && '!border-error focus-visible:!ring-error',              )}
            />
            {createErrors.title != null && (
              <p className="text-xs text-error mt-1">{createErrors.title}</p>
            )}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-muted-foreground">
              {t('wizard.inlineCreate.intent.bodyLabel', 'Script Body')} <span className="text-red-500">*</span>
            </label>
            <textarea
              value={createBody}
              onChange={(e) => {
                setCreateBody(e.target.value);
                capturePrompt.current = e.target.value;
                validateCreateField('body', e.target.value);
              }}
              placeholder={t('intentForm.Write your intent here', 'Write your intent here...')}
              disabled={isSaving || isGeneratingAI}
              className={cn(
                'flex min-h-[160px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 resize-y',
                createErrors.body != null && '!border-error',
              )}
            />
            {createErrors.body != null && (
              <p className="text-xs text-error mt-1">{createErrors.body}</p>
            )}
          </div>

          {featureFlags?.aiScripts && (
            <button
              type="button"
              onClick={() => {
                void handleGenerateAI(); 
              }}
              disabled={isSaving || isGeneratingAI}
              className="flex items-center gap-2 text-sm font-medium text-primary hover:text-primary/80 disabled:opacity-50"
            >
              {isGeneratingAI ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2a2 2 0 0 1 2 2c0 .74-.4 1.39-1 1.73V7h1a7 7 0 0 1 7 7h1a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-1.27A7 7 0 0 1 14 23h-4a7 7 0 0 1-6.73-4H2a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1h1a7 7 0 0 1 7-7h1V5.73c-.6-.34-1-.99-1-1.73a2 2 0 0 1 2-2z" />
                </svg>
              )}
              {t('wizard.inlineCreate.intent.generateAI', 'Generate with AI')}
            </button>
          )}
        </div>
      )}

      <IntentPreviewModal
        isOpen={previewIntent !== null}
        onOpenChange={(open) => {
          if (!open) {
            setPreviewIntent(null);
          }
        }}
        intent={previewIntent}
      />
      <DiscardIntentDialog
        open={showDiscardModal}
        onOpenChange={setShowDiscardModal}
        onConfirm={confirmDiscard}
      />
    </div>
  );
}
